// Domain model of the client portal. It is the portal's own vocabulary, not the
// shape of any backend: the BFF maps Invoice Ninja (money, documents), the chat
// engine and the secrets store onto these types.

/** Which month a retainer's invoice is for, relative to the month it goes out in. */
export type Covers = 'next' | 'current' | 'previous';

export type Money = { amountMinor: number; currency: 'RUB' | 'KZT' | 'USD' };

export type Person = {
  id: string;
  name: string;
  role: string;
  side: 'studio' | 'client';
  initials: string;
};

/** Client roles (MK42-5783): owner — everything; member — work, no money; accountant — money and documents. */
export type ClientRole = 'owner' | 'member' | 'accountant';
/** Studio roles: owner — everything incl. team and audit; manager — own clients; specialist — own projects. */
export type StudioRole = 'owner' | 'manager' | 'specialist';

export type Workspace = {
  id: string;
  clientName: string;
  me: Person & { clientRole: ClientRole };
  people: Person[];
  studio: { name: string; manager: Person };
  /** What this contour offers: RU pays through Точка and writes e-mail only; other contours differ. */
  contour: ContourInfo;
  /** The studio switched the public API on for the company: the owner sees «Интеграции» (MK42-6384). */
  apiEnabled?: boolean;
};

export type PayMethod = 'sbp' | 'card';
export type TransferDetails = { payee: string; taxId: string; bank: string; bic: string; correspondentAccount: string; account: string };
export type PaymentOptions = { provider: 'tochka' | 'paddle' | 'none'; online: PayMethod[]; transfer: TransferDetails | null };

/** One requisite of a payer, as the contour's config describes it (portal-bff config/<contour>.yaml → legal). */
export type LegalField = {
  key: string;
  label: string;
  required: boolean;
  kind: 'text' | 'country';
  wide: boolean;
  numeric: boolean;
  hint: string;
  /** The Client column the value is mirrored into: legal_name, tax_id. */
  column: 'legal_name' | 'tax_id' | null;
};

/** Requisites schema and legal wording of a contour. RU: ИНН/КПП, acts, ГОСТ QR; main: country, VAT id, no acts. */
export type LegalSchema = {
  /** The contour's single country (RU), or null when the client picks it. */
  country: string | null;
  fields: LegalField[];
  /** ISO 3166 alpha-2 codes for a country field. */
  countries?: string[];
  /** Acts of completed work after payment — Russian paperwork, RU only. */
  acts: boolean;
  totalLabel: string;
  taxNote: string;
  /** Printed under the invoice total; empty — nothing. */
  invoiceNote: string;
  payerTitle: string;
  /** Transfer QR standard: 'ST00012' (ГОСТ Р 56042-2014) or none. */
  transferQr: 'ST00012' | null;
  /** What the work rests on when a project names no contract: «Счёт-оферта» in RU. */
  basis: string;
  /** Transfer purpose, with {number} and {date}. */
  paymentPurpose: string;
};

export type ContourInfo = {
  code: 'ru' | 'main';
  currency: Money['currency'];
  channels: ('email' | 'telegram')[];
  /** A bank statement to reconcile: only the RU contour has one. */
  statement: boolean;
  payments: PaymentOptions;
  legal: LegalSchema;
  /** Offer and privacy policy of the contour (config `docs`); a key is missing when the contour has none. */
  docs?: { offer?: string; privacy?: string };
};

/** Requisites by the keys of the contour's schema. */
export type LegalValues = Record<string, string>;

export type ProjectHealth = 'on_track' | 'waiting_client' | 'at_risk' | 'paused' | 'done';

export type Milestone = {
  id: string;
  title: string;
  state: 'done' | 'current' | 'next';
  due?: string;
};

/** Recurring work of a retainer: what happens, how often, when next. */
export type RhythmEvery = 'week' | '2weeks' | 'month';
export type RhythmSource = 'report' | 'meeting' | 'linear' | 'manual';

/**
 * «Было» and «Будет» are computed by the server from the source: reports, meetings, tasks of
 * Linear with a label, or the studio's «Сделано». `cadence` is the text to show for «Как часто»
 * (the old free text when it is not one of the usual phrases).
 */
export type Rhythm = {
  id: string;
  title: string;
  cadence: string;
  every?: RhythmEvery | null;
  source?: RhythmSource;
  /** The Linear label of a `linear` source. */
  label?: string;
  next?: string | null;
  lastDone?: string | null;
  overdue?: boolean;
  /** Linear task keys of a `linear` source: open ones first, then the latest done. */
  taskKeys?: string[];
};

/** What the studio sends for a regular work; the id stays so the «Сделано» mark survives an edit. */
export type RhythmInput = { id?: string; title: string; every: RhythmEvery; source: RhythmSource; label?: string };

export type Metric = {
  id: string;
  label: string;
  value: string;
  /** Change vs the previous period, already phrased: «+2 к прошлой неделе». */
  change?: string;
  trend?: 'up' | 'down' | 'flat';
  /** Good direction for the trend, so the UI knows whether «down» is a win. */
  better?: 'up' | 'down';
  series: number[];
  period: string;
};

type ProjectBase = {
  id: string;
  clientId: string;
  /** Linear project the tasks are synced from. */
  linear?: { name: string; url: string };
  key: string;
  title: string;
  summary: string;
  health: ProjectHealth;
  /** One sentence on where the project is right now. */
  now: string;
  owner: Person;
  team: Person[];
  updatedAt: string;
  /** The payer its invoices go to; null/absent — the client's default. */
  payerId?: string | null;
  terms: {
    price: Money;
    contract?: string;
    since: string;
    /** The last day of the agreed term (set by the studio). */
    until?: string;
  };
};

/** Fixed-scope engagement: price agreed up front, progress is a sequence of milestones. */
export type FixedProject = ProjectBase & {
  kind: 'fixed';
  milestones: Milestone[];
  finishBy: string;
};

/** Ongoing service: no finish line, progress is rhythm and results per period. */
export type RetainerProject = ProjectBase & {
  kind: 'retainer';
  period: string;
  rhythm: Rhythm[];
  metrics: Metric[];
  terms: ProjectBase['terms'] & {
    period: 'month';
    nextInvoiceAt: string;
    /** End of the agreed term; renewal is offered two weeks before. */
    until?: string;
    /** Invoice schedule: on the billingDay-th, an invoice for the month billingCovers says (lib/billing). */
    billingDay?: number;
    billingCovers?: Covers;
    nextPeriod?: string;
    lastInvoicedPeriod?: string;
  };
};

export type Project = FixedProject | RetainerProject;

export type TaskStatus = 'backlog' | 'todo' | 'in_progress' | 'review' | 'done';

export type Task = {
  id: string;
  key: string;
  projectId: string;
  /** Tasks live in Linear; only those marked client-visible reach the client. */
  clientVisible: boolean;
  /** Linear label portal_for_client shows the task to the client; the studio toggle cannot hide it. */
  visibleByLabel?: boolean;
  linearUrl?: string;
  /** Milestone for fixed projects, stream («Реклама», «Аналитика») for retainers. */
  group: string;
  title: string;
  description: string;
  status: TaskStatus;
  needsClient?: { kind: 'approve' | 'answer' | 'materials' | 'access'; note: string };
  due?: string;
  assignee: Person;
  threadId: string;
  comments: number;
  unread: number;
  attachments: Attachment[];
  createdAt: string;
  updatedAt: string;
};

export type Attachment = { id: string; name: string; sizeKb: number };

export type Message = {
  id: string;
  threadId: string;
  author: Person;
  body: string;
  at: string;
  attachments: Attachment[];
};

export type MessagePage = { items: Message[]; hasEarlier: boolean; total: number };

export type Thread = {
  id: string;
  projectId: string;
  taskId?: string;
  title: string;
  lastMessageAt: string;
  lastMessage: string;
  lastAuthor?: Person;
  unread: number;
  total: number;
  /** The last word is the studio's question and nobody on the client side replied. */
  awaitingClient: boolean;
};

export type Activity = {
  id: string;
  projectId: string;
  at: string;
  who: Person;
  text: string;
  taskId?: string;
  kind: 'done' | 'status' | 'file' | 'comment' | 'money';
};

/** `cancelled` is only ever seen when a client opens a cancelled invoice of their own by a link: it is in no list. */
export type InvoiceStatus = 'draft' | 'sent' | 'processing' | 'paid' | 'overdue' | 'cancelled';

export type InvoiceLine = { title: string; note?: string; quantity: number; /** «усл.», «шт.», «ч»…; absent in the demo's old invoices. */ unit?: string; price: Money; total: Money };

/** A line as the studio types it: price in minor units, quantity up to 3 decimals. */
export type LineInput = { title: string; description?: string; quantity: number; unit: string; price: number };
/** Units the forms offer; any text up to 10 characters is accepted. */
export const LINE_UNITS = ['усл.', 'шт.', 'ч', 'мес.', 'день'] as const;

export type InvoiceInput = {
  projectId?: string;
  /** Catalog services issued before free lines: still accepted by the BFF. */
  items?: { catalogId: string; note?: string }[];
  lines?: LineInput[];
  dueDays?: number;
  /** ISO date; wins over dueDays. */
  dueDate?: string;
  /** The comment to the client, printed on the invoice. */
  comment?: string;
  /** «Договор № …», «Запрос «…»», «Этап «…»». */
  basis?: string;
};
export type IssueInvoiceInput = InvoiceInput & {
  clientId: string;
  /** Save a draft the client does not see. */
  draft?: boolean;
  /** The legal entity billed; left out — the project's payer, else the client's default. */
  payerId?: string;
};

export type PaymentMethod = 'card' | 'sbp' | 'transfer' | 'cash';

/** How the studio records a payment by hand; `other` is booked as a transfer. */
export type ManualPaymentMethod = 'transfer' | 'cash' | 'card' | 'other';
export type MarkPaidInput = { date?: string; amountMinor?: number; method: ManualPaymentMethod; note?: string };

export type Invoice = {
  id: string;
  number: string;
  issuedAt: string;
  dueAt: string;
  status: InvoiceStatus;
  total: Money;
  paid: Money;
  lines: InvoiceLine[];
  projectId?: string;
  projectTitle?: string;
  /** The client's legal entity the invoice is made out to. */
  payer?: InvoicePayer | null;
  actId?: string;
  actNumber?: string | null;
  actDocumentId?: string | null;
  /** The act that closes the invoice; the client does not see a draft. */
  actStatus?: ActStatus | null;
  basis?: string | null;
  comment?: string | null;
  payment?: { at: string; method: PaymentMethod; reference: string; receipt?: string };
  // Only in the studio's list.
  clientId?: string | null;
  clientName?: string | null;
  /** When the client was last reminded (manual or automatic). */
  remindedAt?: string | null;
  /** The invoice in Invoice Ninja's own app; absent when the BFF does not know its public address. */
  inUrl?: string | null;
};

export type ActStatus = 'draft' | 'to_sign' | 'signed' | 'void';

export type ActLine = { title: string; description?: string | null; quantity: number; unit: string; price: Money; total: Money; /** Where the line came from: milestone:…, task:MK42-7, invoice:…; empty for a free line. */ source?: string | null };

export type Act = {
  id: string;
  number: string;
  issuedAt: string;
  /** The first invoice the act closes (older acts have exactly one); null when it closes none. */
  invoiceId: string | null;
  invoiceIds?: string[];
  invoiceNumbers?: string[];
  total: Money;
  status: ActStatus;
  signedAt?: string | null;
  signedBy?: string | null;
  /** portal: the client clicked; scan: the client uploaded a signed scan; paper | edo: the studio marked it. */
  signedVia?: 'portal' | 'scan' | 'paper' | 'edo' | null;
  /** The name the owner typed when signing in the portal. */
  signedName?: string | null;
  /** SHA-256 of the signed file: the act PDF (portal) or the scan. */
  signedHash?: string | null;
  /** Studio view only: who signed (the person's name in the cabinet). */
  signerName?: string | null;
  /** The stored PDF; absent until the act is rendered. */
  documentId?: string | null;
  scanDocumentId?: string | null;
  fileName: string;
  projectId?: string | null;
  projectTitle?: string | null;
  periodFrom?: string | null;
  periodTo?: string | null;
  basis?: string | null;
  summary?: string;
  lines?: ActLine[];
  // Only in the studio's list.
  clientId?: string;
  clientName?: string | null;
  actsEnabled?: boolean | null;
};

export type ActInput = {
  projectId?: string | null;
  periodFrom?: string | null;
  periodTo?: string | null;
  number?: string;
  date?: string | null;
  basis?: string;
  summary?: string;
  lines: (LineInput & { source?: string })[];
  invoiceIds: string[];
};

/** A source of act lines: `line` is ready to drop into the editor. */
export type ActSourceLine = LineInput & { source: string };
export type ActSources = {
  milestones: { id: string; projectId: string; projectTitle: string; due?: string | null; used: boolean; line: ActSourceLine }[];
  tasks: { id: string; key: string; url: string; projectId: string; projectTitle: string; doneAt: string; used: boolean; line: ActSourceLine }[];
  tasksUnavailable: boolean;
  invoices: { id: string; number: string; status: InvoiceStatus; issuedAt: string; projectId?: string | null; total: Money; lines: ActSourceLine[] }[];
};

export type Subscription = {
  id: string;
  title: string;
  price: Money;
  period: 'month';
  status: 'active' | 'paused';
  nextInvoiceAt: string;
  since: string;
  projectId?: string;
};

export type DocumentKind = 'brief' | 'design' | 'contract' | 'sign' | 'act' | 'act_scan' | 'report' | 'other';

export type DocumentRef = {
  id: string;
  name: string;
  kind: DocumentKind;
  projectId?: string;
  uploadedBy: Person;
  uploadedAt: string;
  sizeKb: number;
};

export type SecretStatus = 'waiting' | 'opened' | 'expired' | 'revoked';

/** Metadata only. The value is encrypted in the sender's browser and never listed. */
export type Secret = {
  id: string;
  title: string;
  from: Person;
  to: Person;
  projectId?: string;
  createdAt: string;
  expiresAt: string;
  status: SecretStatus;
  openedAt?: string;
};

export type ActionItem =
  | { kind: 'task'; task: Task }
  | { kind: 'invoice'; invoice: Invoice }
  | { kind: 'act'; act: Act }
  | { kind: 'secret'; secret: Secret }
  | { kind: 'reply'; thread: Thread }
  | { kind: 'proposal'; proposal: Proposal }
  | { kind: 'sign'; sign: SignRequest }
  | { kind: 'onboarding'; item: OnboardingItem }
  | { kind: 'change'; change: ChangeRequest }
  | { kind: 'feedback'; projectId: string; context: string }
  /** A payer of an active project lacks what its invoices should carry (КПП of an ООО): never a blocker. */
  | { kind: 'payer'; payer: PayerSummary; text: string };

/** What `/actions` really returns: the list plus a marker when Invoice Ninja did not answer (the invoices are then missing, not absent). */
export type RawAction = ActionItem | { kind: 'moneyUnavailable' };

export type SearchHit = { kind: 'task' | 'thread' | 'document' | 'invoice'; id: string; title: string; hint: string; to: string };

export type CheckoutStatus = 'pending' | 'paid' | 'expired' | 'refunded' | 'mismatch';
/** An online payment at the contour's provider. Live: `redirectUrl` is the bank's page (card or SBP). */
export type CheckoutSession = { id: string; invoiceId: string; method: PayMethod; amount: Money; status: CheckoutStatus; redirectUrl?: string | null; qrPayload?: string };

// ---------------------------------------------------------------------------
// Studio side

export type Contour = 'ru' | 'main';

export type ClientContact = { person: Person; email: string; /** The studio's card of a client sees the contacts the person gave themselves. */ phone?: string; messenger?: string; role: ClientRole; invited: 'pending' | 'accepted'; disabled?: boolean };

/** The answer to inviting, changing or re-inviting a colleague: a line to show and the fresh list. */
export type ColleaguesReply = { detail: string; colleagues: ClientContact[] };

export type ColleaguePatch = { role?: ClientRole; disabled?: boolean };

/** One browser session of a person; never the token. */
export type PersonSession = { createdAt: string; lastSeenAt: string; expiresAt: string };

/** A studio person as the team list shows them; e-mail and access state come only to owners. */
export type TeamMember = Person & {
  studioRole: StudioRole;
  email?: string;
  invited?: 'pending' | 'accepted';
  lastSeen?: string | null;
  disabled?: boolean;
};

/** Anyone who can sign in, either side, with what they may do. Owners only. */
export type PersonAccess = {
  person: Person;
  email: string;
  side: 'studio' | 'client';
  studioRole: StudioRole | null;
  clientRole: ClientRole | null;
  clientId: string | null;
  clientName: string | null;
  invited: 'pending' | 'accepted';
  lastSeen: string | null;
  disabled: boolean;
};

export type PersonPatch = { studioRole?: StudioRole; clientRole?: ClientRole; disabled?: boolean; name?: string; email?: string };

/** «Мой профиль»: the caller's own contacts. The sign-in e-mail is shown, not edited. */
export type MyProfile = Person & { email: string; phone: string; messenger: string };
export type ProfilePatch = { name?: string; role?: string; phone?: string; messenger?: string };
/** One of the caller's own browser sessions; `current` is the one asking. */
export type MySession = PersonSession & { current: boolean };

/** Another person's card. The BFF answers 404 for anyone the viewer may not open. */
export type PersonCard = Person & {
  email: string;
  phone: string;
  messenger: string;
  clientId: string | null;
  clientName: string | null;
  studioRole: StudioRole | null;
  clientRole: ClientRole | null;
  disabled: boolean;
  projects: { id: string; title: string; clientId: string; lead: boolean }[];
};

export type AuditAction =
  | 'login'
  | 'logout'
  | 'invite'
  | 'role_changed'
  | 'disabled'
  | 'enabled'
  | 'sessions_revoked'
  | 'colleague_role_changed'
  | 'colleague_disabled'
  | 'colleague_enabled'
  | 'invite_conflict'
  | 'person_edited'
  | 'person_anonymised'
  | 'secret_opened'
  | 'notification_retried'
  | 'offboarded'
  | 'act_signed'
  | 'document_signed'
  | 'sign_request_created'
  | 'payment_problem_resolved'
  | 'client_created'
  | 'client_updated'
  | 'invoice_issued'
  | 'invoice_reminded'
  | 'invoice_cancelled'
  | 'retainer_invoiced'
  | 'statement_added'
  | 'statement_matched'
  | 'statement_unmatched'
  | 'statement_ignored'
  | 'statement_imported'
  | 'act_reissued'
  | 'project_created'
  | 'project_updated'
  | 'project_linear_linked'
  | 'contact_added'
  | 'tasks_visibility'
  | 'project_renewed'
  | 'now_updated'
  | 'proposal_created'
  | 'proposal_accepted'
  | 'proposal_declined'
  | 'change_requested'
  | 'change_estimated'
  | 'change_decided'
  | 'change_reinvoiced'
  | 'secret_sent'
  | 'secret_revoked'
  | 'secret_rerequested'
  | 'document_uploaded'
  | 'task_visibility'
  | 'task_needs_set'
  | 'task_needs_cleared'
  | 'task_approved'
  | 'report_published'
  | 'meeting_created'
  | 'meeting_updated'
  | 'meeting_canceled'
  | 'onboarding_toggled'
  | 'onboarding_completed'
  | 'payer_saved'
  | 'checkout_started'
  | 'transfer_reported'
  | 'change_declined'
  | 'change_asked'
  | 'change_answered'
  | 'change_parked'
  | 'project_deleted'
  | 'project_linear_changed'
  | 'project_linear_unlinked'
  | 'rhythm_done'
  | 'proposal_drafted'
  | 'proposal_sent'
  | 'proposal_withdrawn'
  | 'proposal_lost'
  | 'report_updated'
  | 'report_drafted'
  | 'report_draft_updated'
  | 'thread_closed'
  | 'thread_created'
  | 'profile_updated'
  | 'invoice_draft_saved'
  | 'invoice_draft_updated'
  | 'invoice_draft_deleted'
  | 'invoice_sent'
  | 'act_drafted'
  | 'act_updated'
  | 'act_deleted'
  | 'act_sent'
  | 'act_voided'
  | 'act_marked_signed'
  | 'act_scan_uploaded';

export type AuditEvent = {
  id: number;
  at: string;
  action: AuditAction;
  actor: Person | null;
  target: Person | null;
  clientId: string | null;
  clientName: string | null;
  data: Record<string, unknown>;
};

/** Owners filter the log: person id, client id, action prefix, and a day or time range (ISO). */
export type AuditFilter = { actor?: string; clientId?: string; action?: string; since?: string; until?: string };

/** Newest first; `next` is the id to pass as `before` for the following page. */
export type AuditPage = { items: AuditEvent[]; next: number | null };

export type Client = {
  id: string;
  name: string;
  legalName: string;
  taxId: string;
  /** Every requisite by the contour's schema. */
  legal: LegalValues;
  contour: Contour;
  stage: 'lead' | 'proposal' | 'active' | 'paused' | 'former';
  /** Acts on paid invoices where the contour has them; off when paying the invoice is the acceptance (MK42-5898). */
  actsEnabled?: boolean;
  /** The acceptance smoke's own client (demo only marks it; the studio's «Интеграции» answer `isTest`). */
  isTest?: boolean;
  /** The public API for this client (off by default); off, and none of its keys works. */
  apiEnabled?: boolean;
  /** The legal entities invoices are made out to, the default first (its requisites are `legal`). */
  payers?: PayerSummary[];
  contacts: ClientContact[];
  since: string;
  manager: Person;
};

export type ClientSummary = Client & {
  projects: number;
  mrr: Money;
  /** null while Invoice Ninja does not answer (or the role has no money): never a made-up zero. */
  receivable: Money | null;
  overdue: Money | null;
  waitingOnUs: number;
  waitingOnClient: number;
  lastContactAt?: string;
};

export type CatalogItem = { id: string; title: string; note: string; price: Money; kind: 'fixed' | 'subscription' };

export type CheckoutProblemKind = 'amount_mismatch' | 'overpaid' | 'record_failed' | 'paid_after_expiry';

/** A notification the portal gave up sending to someone (MK42-5818); the studio can send it again. */
export type UndeliveredNotification = {
  id: string;
  at: string;
  text: string;
  audience: 'client' | 'studio';
  clientName: string | null;
  /** Only those it failed for, with how many times it was tried. */
  recipients: { person: Pick<Person, 'id' | 'name'>; tries: number }[];
};

/** An online payment the portal could not settle cleanly (MK42-5813). Money may be at the bank, unrecorded. */
export type CheckoutProblem = {
  id: string;
  clientId: string | null;
  invoiceId: string;
  invoiceNumber: string | null;
  provider: string;
  operationId: string;
  status: string;
  problem: CheckoutProblemKind | null;
  amount: Money;
  bankAmount: Money | null;
  overpaid: Money | null;
  createdAt: string | null;
  problemAt: string | null;
  settledAt: string | null;
  resolvedAt: string | null;
  resolvedNote: string | null;
};

/** A conversation in the studio's inbox. `waitingOnUs`: the client has the last word and nobody answered, «?» or not. */
/** «Новое обсуждение»: a thread of its own, optionally about one task; the first message opens it. */
export type NewDiscussion = { projectId: string; title: string; taskId?: string; body: string };

export type StudioThread = Thread & {
  client: string;
  clientId: string;
  waitingOnUs: boolean;
  /** Whole hours since the client's last message while it waits; null otherwise. */
  waitingHours: number | null;
  /** Closed without a reply: stays hidden from the queue until the client writes again. */
  closed: boolean;
};

export type StudioThreadFilter = { q?: string; clientId?: string; projectId?: string; waiting?: boolean };

export type StudioAttention =
  | { kind: 'reply'; thread: StudioThread; client: string; waitingHours: number }
  | { kind: 'overdue'; invoice: Invoice; client: string }
  | { kind: 'act_missing'; invoice: Invoice; client: string }
  /** A retainer whose invoice date has come while automatic invoicing is off: issue it by hand. */
  | { kind: 'retainer_due'; project: { id: string; title: string }; client: string; clientId: string; dueAt: string; price: Money }
  | { kind: 'payment_issue'; checkout: CheckoutProblem; invoiceId: string; link: string; client: string }
  | { kind: 'stale_client'; task: Task; client: string; days: number }
  | { kind: 'now_outdated'; project: Project; client: string; days: number }
  | { kind: 'rhythm'; project: { id: string; title: string }; client: string; rhythm: { id: string; title: string; next: string }; days: number }
  | { kind: 'change'; change: ChangeRequest; client: string }
  /** An estimate the client has not answered for 3 days. */
  | { kind: 'change_unanswered'; change: ChangeRequest; client: string; days: number }
  | { kind: 'proposal_silent'; proposal: Proposal; client: string; days: number }
  /** One row per project: `count` open items, `item` is the oldest. */
  | { kind: 'onboarding'; project: { id: string; title: string }; count: number; item: OnboardingItem; client: string }
  /** An access a client sent to this person and nobody opened yet. */
  | { kind: 'secret'; secret: Secret; clientId: string | null; client: string }
  /** A payer of an active project lacks requisites its invoices should carry; the client is asked too. */
  | { kind: 'payer_incomplete'; payer: PayerSummary; client: string; clientId: string }
  /** A webhook the portal switched off after 3 days without a 2xx; the client is told too. */
  | { kind: 'webhook_disabled'; endpoint: { id: string; name: string; url: string; disabledAt: string | null }; client: string; clientId: string };

export type StudioOverview = {
  /** Most urgent first, the oldest first inside a kind. */
  attention: StudioAttention[];
  /** What the sidebar counts: overdue, payment problems, replies waiting over 24 h. */
  urgentCount?: number;
  /** null while the source is down (see `unavailable`): never a made-up zero. */
  revenueMonth: Money | null;
  receivable: Money | null;
  activeClients: number;
  openTasks: number | null;
  /** Sources that did not answer: `money` (Invoice Ninja), `tasks` (Linear). */
  unavailable?: ('money' | 'tasks')[];
};

export type HealthStatus = 'ok' | 'failing' | 'unknown';

export type IntegrationHealth = {
  key: 'invoiceninja' | 'linear' | 'mail' | 'payments' | 'worker' | 'erp';
  name: string;
  status: HealthStatus;
  lastOkAt: string | null;
  lastErrorAt: string | null;
  /** Short error class, never upstream text. */
  error: string | null;
  /** Payment provider only. */
  provider?: string;
  mode?: 'sandbox' | 'live' | null;
  caller?: string | null;
};

export type IntegrationsHealth = { generatedAt: string; dependencies: IntegrationHealth[] };

export type NewClient = {
  name: string;
  legalName?: string;
  taxId?: string;
  /** Requisites by the contour's schema; the studio may fill only what it knows yet. */
  legal?: LegalValues;
  contour: Contour;
  contacts: { name: string; email: string; role: ClientContact['role'] }[];
};

export type NewProject = {
  clientId: string;
  kind: 'fixed' | 'retainer';
  title: string;
  summary: string;
  now: string;
  price: Money;
  ownerId: string;
  teamIds: string[];
  milestones?: { title: string; due?: string }[];
  finishBy?: string;
  rhythm?: Omit<RhythmInput, 'id'>[];
  linearProject?: string;
};

/** Open Linear projects for the picker; `connected` is false when this cabinet has no Linear. */
export type LinearProjects = { connected: boolean; projects: { id: string; name: string; url: string }[] };

export type NewTask = { projectId: string; title: string; description: string; group: string; assigneeId: string; due?: string; clientVisible: boolean };

export type TaskPatch = Partial<Pick<Task, 'clientVisible' | 'status' | 'due'>> & { needsClient?: Task['needsClient'] | null };

/** What the studio can change on a project card (MK42-5831); price and until are for owners and managers. */
export type ProjectPatch = {
  title?: string;
  summary?: string;
  price?: Money;
  milestones?: { id?: string; title: string; due?: string | null; state: Milestone['state'] }[];
  teamIds?: string[];
  ownerId?: string;
  finishBy?: string | null;
  until?: string | null;
  billing?: { day: number; covers: Covers; nextPeriod: string };
  status?: 'active' | 'paused' | 'done';
  rhythm?: RhythmInput[];
  /** A payer of the client; null — the client's default. Owners and managers only. */
  payerId?: string | null;
};

export type ClientPatch = { name?: string; legal?: LegalValues; stage?: 'lead' | 'active' | 'paused'; manager_id?: string; actsEnabled?: boolean; apiEnabled?: boolean };

/** A document as the studio lists it: the client it belongs to is named. */
export type StudioDocument = DocumentRef & { clientId: string };

// ---------------------------------------------------------------------------
// Full client cycle: proposal → contract → onboarding → changes → reports and
// meetings → money → feedback, renewal and offboarding.

export type ProposalStatus = 'draft' | 'sent' | 'accepting' | 'accepted' | 'declined' | 'withdrawn' | 'lost';

export type Proposal = {
  id: string;
  clientId: string;
  number: string;
  title: string;
  intro: string;
  kind: 'fixed' | 'retainer';
  lines: InvoiceLine[];
  total: Money;
  milestones: { title: string; weeks: number }[];
  prepaymentPercent: number;
  validUntil: string;
  status: ProposalStatus;
  sentAt?: string;
  decidedAt?: string;
  declineReason?: string;
  projectId?: string;
};

export type SignKind = 'contract' | 'act' | 'addendum';

export type SignRequest = {
  id: string;
  kind: SignKind;
  title: string;
  documentName: string;
  /** The stored PDF the client opens before signing; absent on old requests, which cannot be signed. */
  documentId?: string;
  projectId?: string;
  createdAt: string;
  status: 'to_sign' | 'signed';
  signedAt?: string;
  signedBy?: string;
  /** Fingerprint of the exact file that was signed — part of the simple e-signature record. */
  hash?: string;
};

export type OnboardingItem = {
  id: string;
  projectId: string;
  kind: 'access' | 'materials' | 'contact' | 'meeting';
  title: string;
  hint: string;
  done: boolean;
  doneAt?: string;
  /** «launch» (default) or «access»: asked outside the launch, shown in «Доступы». */
  scope?: 'launch' | 'access';
  /** The studio person the credentials are addressed to (access scope). */
  toId?: string;
  /** The secret that answered an access request. */
  secretId?: string;
};

/** What the studio project tab «Доступы» lists: requests of both scopes and the secrets sent for the project. */
export type ProjectAccess = {
  requests: (OnboardingItem & { scope: 'launch' | 'access'; status: 'open' | 'done' })[];
  secrets: (Secret & { mine: boolean })[];
};

/** «Идеи и запросы», filed in the studio's Linear inbox on submit; Linear decides: new → (question ⇄ new) → in_scope | estimated → accepted | declined | parked → done. */
export type ChangeStatus = 'new' | 'question' | 'estimated' | 'in_scope' | 'accepted' | 'declined' | 'parked' | 'done';
export type ChangeUrgency = 'low' | 'month' | 'urgent';
/** The direction the studio sees in an initiative; picks the flow label of its Linear task. */
export type InitiativeKind = 'site' | 'ads' | 'analytics' | 'content' | 'other';
export type NewInitiative = { goal?: string; urgency?: ChangeUrgency; documentIds?: string[] };

export type ChangeRequest = {
  id: string;
  projectId: string;
  title: string;
  /** Why, what result the client expects (absent from requests made before the form asked). */
  goal?: string;
  description: string;
  urgency?: ChangeUrgency;
  /** `kind: 'api'` — filed by a client's system through a key («Интеграция «1С»»). */
  author: Person & { kind?: 'api' };
  createdAt: string;
  status: ChangeStatus;
  /** Filed through the API: the id and the link in the client's own system. */
  externalId?: string;
  externalUrl?: string;
  /** The studio's open question (status «question»). */
  question?: string | null;
  /** Why the studio parked it. */
  parkNote?: string;
  kind?: InitiativeKind;
  /** Its Linear task (the studio sees it from submit; the client once it is taken into work). */
  task?: { key: string; id: string };
  /** Studio only: the task in Linear. */
  taskUrl?: string;
  estimate?: { price: Money; days: number; note: string };
  decidedAt?: string;
  invoiceId?: string;
  declineReason?: string;
  /** Set when the studio turned the request down (the client's own refusal has none). */
  declinedBy?: 'studio';
  /** Files that came with the request, when there are any. */
  attachments?: Attachment[];
  /** Earlier invoices of this request, replaced after the studio canceled them. */
  invoiceIds?: string[];
  /** The story so far; absent from lists of requests made before it was kept (the detail rebuilds it). */
  history?: ChangeEvent[];
  /** The request was accepted but its Linear task could not be created (Linear was down): the studio can retry. */
  linearPending?: boolean;
};

export type ChangeEventKind = 'created' | 'estimated' | 'in_scope' | 'declined' | 'accepted' | 'invoice_issued' | 'invoice_canceled' | 'invoice_reissued' | 'asked' | 'answered' | 'parked' | 'task_created' | 'done';

/** One step in the story of a request; `by` is null where nobody is known (an old record). */
export type ChangeEvent = {
  at: string;
  kind: ChangeEventKind;
  by: Person | null;
  price?: Money;
  days?: number;
  note?: string;
  reason?: string;
  side?: 'studio' | 'client';
  invoiceId?: string;
  number?: string;
  previousInvoiceId?: string;
  previousNumber?: string;
  /** A question or an answer. */
  text?: string;
  /** The task's key (task_created). */
  key?: string;
};

/** The invoice of an accepted request: `dead` when it was canceled (then a new one can be issued). */
export type ChangeInvoice = { id: string; number: string | null; status: InvoiceStatus | 'unknown'; dead: boolean; total?: Money; dueAt?: string };

/** One request in full: the whole story and the invoice's state. */
export type ChangeDetail = ChangeRequest & { history: ChangeEvent[]; invoice?: ChangeInvoice; canReinvoice?: boolean };

export type Report = {
  id: string;
  projectId: string;
  period: string;
  /** Absent on a draft: the weekly pipeline files the week as a draft the client does not see (MK42-5899). */
  publishedAt?: string;
  status?: 'draft';
  draftedAt?: string;
  author: Person;
  summary: string;
  numbers: { label: string; value: string; change?: string }[];
  done: string[];
  next: string[];
  fileName?: string;
  /** The report as a file (the weekly deck), a document of the project (MK42-5899). */
  documentId?: string;
};

export type MeetingFreq = 'weekly' | 'biweekly' | 'monthly';
export type MeetingRecurrence = { freq: MeetingFreq; until: string };

export type Meeting = {
  id: string;
  projectId: string;
  /** ISO, UTC. */
  at: string;
  title: string;
  agenda: string[];
  /** A video link the studio pasted (Zoom or any https URL). */
  link?: string | null;
  durationMin?: number;
  /** Absent on meetings from before cancelling existed: they are planned. «held»: ended or has its outcome (derived). */
  status?: 'planned' | 'held' | 'canceled';
  /** Occurrences of one plan share it; each carries the recurrence for display. */
  seriesId?: string;
  recurrence?: MeetingRecurrence;
  outcome?: { summary: string; decisions: string[] };
  /** Studio only: the client's view never has it. */
  transcriptUrl?: string | null;
};

/** What the studio plans; a recurrence makes one meeting per occurrence (up to 26). */
export type MeetingInput = { projectId: string; title: string; at: string; durationMin: number; agenda: string[]; link?: string; recurrence?: MeetingRecurrence };
/** «one»: this meeting only; «following»: this and the later ones of its series. */
export type MeetingScope = 'one' | 'following';
export type MeetingPatch = Partial<Pick<Meeting, 'title' | 'at' | 'durationMin' | 'agenda' | 'link'>>;
/** A draft of the outcome from the Fireflies transcript; the studio reviews it before publishing. */
export type FirefliesDraft = { summary: string; decisions: string[]; transcriptUrl: string; title: string };

export type Notification = {
  id: string;
  at: string;
  text: string;
  to: string;
  read: boolean;
};

export type NotificationPrefs = {
  email: boolean;
  telegram: boolean;
  digest: 'off' | 'daily' | 'weekly';
  /** When this client person closed the first-login guide; the server keeps the first moment. Studio people never have it. */
  tourSeenAt?: string | null;
};

/** The payer's requisites by the contour's schema (workspace.contour.legal.fields) plus the e-mail for invoices. */
export type PayerDetails = LegalValues & { email: string };

/** One legal entity of the client that invoices are made out to; each is its own buyer. */
export type PayerSummary = {
  id: string;
  legal: LegalValues;
  legalName: string;
  taxId: string;
  isDefault: boolean;
  /** Labels of what an invoice should carry and this payer lacks (КПП of an ООО); never a blocker. */
  missing: string[];
  /** Projects whose invoices go to this payer. */
  projects: { id: string; title: string }[];
};

export type InvoicePayer = { id: string; name: string };

export type Feedback = { id: string; projectId: string; score: number; comment: string; at: string; context: string };

export type StatementBasis = 'number' | 'amount' | 'payer';
export type StatementSuggestion = { invoiceId: string; number: string; clientName: string; balance: Money; basis: StatementBasis };
export type StatementAllocation = { invoiceId: string; number: string; amountMinor: number };
export type StatementLine = {
  id: string;
  date: string;
  amount: Money;
  payer: string;
  purpose: string;
  status: 'unmatched' | 'partial' | 'matched' | 'ignored';
  /** One line can pay several invoices. */
  allocations: StatementAllocation[];
  allocated: Money;
  /** What stayed over after the invoices were paid. */
  overpaid?: Money | null;
  suggestions: StatementSuggestion[];
};
/** What one line pays: an invoice and an amount, or no amount for «as much as fits». */
export type StatementPick = { invoiceId: string; amountMinor?: number };
export type StatementImport = { added: number; duplicates: number; unreadable: number };

/** The person's calendar subscription: https feed and the same as webcal://. */
export interface CalendarLink {
  url: string;
  webcalUrl: string;
}

// --- «Интеграции»: keys of the public API (MK42-6384) -------------------------------------------

export type ApiScope = 'read:projects' | 'read:tasks' | 'read:reports' | 'read:initiatives' | 'write:initiatives' | 'read:documents' | 'read:money' | 'write:money';
/** Ready sets of rights; the BFF expands them (api_keys_api.PRESETS). */
export type ApiPreset = 'accounting' | 'reports' | 'ideas' | 'read';

/** A key as the lists show it: never the key itself. */
export type ApiKey = {
  id: string;
  name: string;
  /** The start of the key, to tell keys apart: `m42_ru_live_7Kq…`. */
  prefix: string;
  scopes: ApiScope[];
  projectId: string | null;
  createdAt: string;
  expiresAt: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
  /** This key is a rotation of that one. */
  replacesId: string | null;
  /** This key was rotated: the new one's id (the old one works until `expiresAt`). */
  replacedById: string | null;
  live: boolean;
  createdBy: string | null;
};
/** The answer of issuing or rotating: the only time the key is shown. */
export type IssuedApiKey = ApiKey & { secret: string };
export type NewApiKey = { name: string; preset?: ApiPreset; scopes?: ApiScope[]; projectId?: string };

export type ApiCall = { id: number; at: string; keyId: string | null; keyName: string | null; method: string; route: string; status: number; ms: number; write: boolean };
export type ApiCallsPage = { items: ApiCall[]; total: number; offset: number; limit: number };

/** The studio's «Интеграции» of a client. */
export type ClientIntegrations = {
  clientId: string;
  apiEnabled: boolean;
  /** The acceptance smoke's test client: only for it the studio issues keys itself. */
  isTest: boolean;
  mayIssue: boolean;
  keys: ApiKey[];
  calls: ApiCallsPage;
};

// --- «Интеграции»: webhooks (MK42-6388) ----------------------------------------------------------

/** The types the cabinet offers; the BFF may send others later (an unknown one shows as its code). */
export type WebhookEventType = 'invoice.issued' | 'report.published' | 'task.needs_client' | 'test.ping';
/** Why an endpoint is off: waiting for the first 2xx ping, the ping failed, 3 days without 2xx, by the client, by the studio. */
export type WebhookOffReason = 'unverified' | 'ping_failed' | 'failing' | 'by_user' | 'by_studio';

/** An endpoint as the lists show it: never the secret. */
export type WebhookEndpoint = {
  id: string;
  name: string;
  url: string;
  events: string[];
  enabled: boolean;
  disabledReason: WebhookOffReason | null;
  disabledAt: string | null;
  failureCount: number;
  failingSince: string | null;
  lastSuccessAt: string | null;
  /** The secret was rotated less than 24 h ago: the old one still signs too. */
  secretRotating: boolean;
  createdAt: string;
};
/** The answer of adding an endpoint or rotating its secret: the only time the secret is shown. */
export type IssuedWebhookEndpoint = WebhookEndpoint & { secret: string };
export type NewWebhookEndpoint = { url: string; events: string[]; description?: string };
export type WebhookPatch = { url?: string; events?: string[]; description?: string; enabled?: boolean };
export type WebhookList = { endpoints: WebhookEndpoint[]; events: { type: string; scope: string | null }[]; configured: boolean };

export type WebhookDeliveryStatus = 'pending' | 'delivered' | 'failed' | 'dead';
export type WebhookDelivery = {
  id: string;
  endpointId: string;
  eventId: string;
  type: string;
  status: WebhookDeliveryStatus;
  attempt: number;
  nextAttemptAt: string | null;
  responseCode: number | null;
  error: string | null;
  responseExcerpt: string | null;
  durationMs: number | null;
  createdAt: string;
  deliveredAt: string | null;
  replayOf: string | null;
};
export type WebhookDeliveryFilter = { endpointId?: string; status?: WebhookDeliveryStatus; event?: string; before?: string; limit?: number };
/** Newest first; `nextBefore` asks for the older page. */
export type WebhookDeliveriesPage = { items: WebhookDelivery[]; nextBefore: string | null };
export type WebhookPing = { delivery: WebhookDelivery; endpoint: WebhookEndpoint };
/** The studio's read-only view of a client's webhooks. */
export type ClientWebhooks = { endpoints: WebhookEndpoint[]; apiEnabled: boolean };
